import React, { useState, useEffect, useRef } from 'react';
import StatusActionButton from '../buttons/StatusActionButton';
import { useAuth } from '../../contexts/AuthContext';
import ScrollableContainer from '../ScrollableContainer';
import config from '../../../config/config';
import './Messenger.css';

export interface Message {
  id: number | string;
  userId: number;
  userName: string;
  text: string;
  createdAt: string;
  isEdited: boolean;
  editedAt?: string;
  canEdit: boolean;
  canDelete: boolean;
  isPinned?: boolean;
  editDisabledReason?: string;
  avatarUrl?: string; // URL аватарки пользователя (размер medium)
}

export interface MessengerProps {
  messages: Message[];
  loading?: boolean;
  error?: string | null;
  onErrorClose?: () => void;
  onSendMessage: (text: string) => Promise<void>;
  onEditMessage?: (messageId: number | string, newText: string) => Promise<void>;
  onDeleteMessage?: (messageId: number | string) => Promise<void>;
  onStartEdit?: (messageId: number | string) => boolean; // Возвращает true если можно редактировать
  placeholder?: string;
  maxLength?: number;
  className?: string;
  headerContent?: React.ReactNode; // Дополнительный контент сверху
}

const Messenger: React.FC<MessengerProps> = ({
  messages,
  loading = false,
  error: externalError = null,
  onErrorClose,
  onSendMessage,
  onEditMessage,
  onDeleteMessage,
  onStartEdit,
  placeholder = "Введите сообщение...",
  maxLength = 1000,
  className = '',
  headerContent
}) => {
  const { user } = useAuth();
  const [newMessage, setNewMessage] = useState('');
  const [editingMessageId, setEditingMessageId] = useState<number | string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState<number | string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showActionsForMessage, setShowActionsForMessage] = useState<number | string | null>(null);
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Проверка, является ли сообщение своим
  const isOwnMessage = (message: Message) => {
    return user && message.userId === user.id;
  };

  // Автопрокрутка к последнему сообщению
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Закрытие кнопок действий при клике вне сообщений
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.messenger-message') && !target.closest('.message-actions')) {
        setShowActionsForMessage(null);
      }
    };

    if (showActionsForMessage !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showActionsForMessage]);

  // Автоматическое закрытие ошибки через 2 секунды
  useEffect(() => {
    if (externalError) {
      const timer = setTimeout(() => {
        if (onErrorClose) {
          onErrorClose();
        }
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [externalError, onErrorClose]);

  // Задержка показа индикатора загрузки для предотвращения моргания
  useEffect(() => {
    if (loading && messages.length === 0) {
      const timer = setTimeout(() => {
        setShowLoadingIndicator(true);
      }, 300); // Показываем только если загрузка длится больше 300ms

      return () => {
        clearTimeout(timer);
        setShowLoadingIndicator(false);
      };
    } else {
      setShowLoadingIndicator(false);
    }
  }, [loading, messages.length]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Обработка отправки сообщения
  const handleSendMessage = async () => {
    if (!newMessage.trim() || isSending) return;

    try {
      setError(null);
      setIsSending(true);
      
      if (editingMessageId) {
        // Режим редактирования
        await handleSaveEdit();
      } else {
        // Обычная отправка
        await onSendMessage(newMessage.trim());
        setNewMessage('');
        
        // Важно: возвращаем фокус на input после отправки
        // Это предотвращает сворачивание клавиатуры на мобильных устройствах
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 0);
      }
    } catch (error) {
      console.error('Ошибка отправки сообщения:', error);
      setError('Ошибка отправки сообщения');
    } finally {
      setIsSending(false);
    }
  };

  // Обработка нажатия Enter
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Начало редактирования сообщения
  const handleStartEdit = (message: Message) => {
    // Проверяем, можно ли редактировать сообщение
    if (onStartEdit && !onStartEdit(message.id)) {
      return; // Не начинаем редактирование, если проверка не прошла
    }
    
    setEditingMessageId(message.id);
    setNewMessage(message.text);
    setShowActionsForMessage(null);
    // Фокусируемся на поле ввода
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(message.text.length, message.text.length);
      }
    }, 0);
  };

  // Сохранение редактирования
  const handleSaveEdit = async () => {
    if (!newMessage.trim() || !editingMessageId || !onEditMessage || isEditing) return;

    try {
      setError(null);
      setIsEditing(true);
      await onEditMessage(editingMessageId, newMessage.trim());
      setEditingMessageId(null);
      setNewMessage('');
      
      // Важно: возвращаем фокус на input после редактирования
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);
    } catch (error) {
      console.error('Ошибка редактирования сообщения:', error);
      setError('Ошибка редактирования сообщения');
    } finally {
      setIsEditing(false);
    }
  };

  // Отмена редактирования
  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setNewMessage('');
  };

  // Обработка клика на сообщение для показа/скрытия кнопок
  const handleMessageClick = (messageId: number | string) => {
    if (showActionsForMessage === messageId) {
      setShowActionsForMessage(null);
    } else {
      setShowActionsForMessage(messageId);
    }
  };

  // Удаление сообщения
  const handleDeleteMessage = async (messageId: number | string) => {
    if (!onDeleteMessage || isDeleting === messageId) return;

    if (!window.confirm('Вы уверены, что хотите удалить это сообщение?')) return;

    try {
      setIsDeleting(messageId);
      await onDeleteMessage(messageId);
    } catch (error) {
      console.error('Ошибка удаления сообщения:', error);
    } finally {
      setIsDeleting(null);
    }
  };

  // Форматирование времени
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diff / (1000 * 60));
    const diffHours = Math.floor(diff / (1000 * 60 * 60));
    const diffDays = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) return 'только что';
    if (diffMinutes < 60) return `${diffMinutes}м назад`;
    if (diffHours < 24) return `${diffHours}ч назад`;
    if (diffDays < 7) return `${diffDays}д назад`;
    
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Получение инициалов пользователя
  const getUserInitials = (name: string): string => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Получение цвета для аватарки
  const getAvatarColor = (userId: number): string => {
    const colors = [
      '#ef4444', '#f97316', '#f59e0b', '#eab308',
      '#84cc16', '#22c55e', '#10b981', '#06b6d4',
      '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6',
      '#a855f7', '#d946ef', '#ec4899', '#f43f5e'
    ];
    const index = userId.toString().split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[index % colors.length];
  };

  return (
    <div className={`messenger ${className}`}>
      {/* Отображение ошибок */}
      {(error || externalError) && (
        <div className="messenger-error">
          <i className="ri-error-warning-line"></i>
          <span>{error || externalError}</span>
          <button 
            className="error-close"
            onClick={() => {
              if (externalError && onErrorClose) {
                onErrorClose();
              } else {
                setError(null);
              }
            }}
          >
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}

      {/* Список сообщений */}
      <ScrollableContainer className="messenger-messages">
        {/* Дополнительный контент сверху */}
        {headerContent && (
          <div className="messenger-header-content">
            {headerContent}
          </div>
        )}

        {showLoadingIndicator && loading && messages.length === 0 ? (
          <div className="messenger-loading">
            <i className="ri-loader-4-line ri-spin"></i>
            <span>Загрузка сообщений...</span>
          </div>
        ) : messages.length === 0 && !loading ? (
          <div className="messenger-empty">
            <i className="ri-message-3-line"></i>
            <span>Сообщений пока нет</span>
            <p>Начните обсуждение, отправив первое сообщение</p>
          </div>
        ) : (
          messages.map((message) => {
            const isOwn = isOwnMessage(message);
            const apiBaseUrl = config.api.baseURL.replace('/api/v1', '').replace('/api/v2', '');
            const avatarUrl = message.avatarUrl 
              ? (message.avatarUrl.startsWith('http') ? message.avatarUrl : `${apiBaseUrl}${message.avatarUrl}`)
              : null;
            
            return (
              <div 
                key={message.id} 
                className={`messenger-message ${message.isPinned ? 'messenger-message--pinned' : (isOwn ? 'messenger-message--own' : 'messenger-message--other')}`}
                onClick={() => handleMessageClick(message.id)}
              >
                {!isOwn && (
                  <div className="message-avatar">
                    {avatarUrl ? (
                      <img 
                        src={avatarUrl}
                        alt={message.userName}
                        className="message-avatar-image"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const fallback = target.nextElementSibling as HTMLElement;
                          if (fallback) {
                            fallback.style.display = 'flex';
                          }
                        }}
                      />
                    ) : null}
                    <div 
                      className="message-avatar-fallback"
                      style={{ 
                        background: getAvatarColor(message.userId),
                        display: avatarUrl ? 'none' : 'flex'
                      }}
                    >
                      {getUserInitials(message.userName)}
                    </div>
                  </div>
                )}
                <div className="message-content-wrapper">
                  {!isOwn && (
                    <div className="message-author">{message.userName}</div>
                  )}
                  <div className="message-content">
                    <div className="message-text">{message.text}</div>
                  </div>
                  
                  <div className="message-meta">
                    <span className="message-time">{formatTime(message.createdAt)}</span>
                    {message.isEdited && (
                      <span className="message-edited">(изменено)</span>
                    )}
                  </div>
                </div>

                {/* Действия с сообщением */}
                {(message.canEdit || message.canDelete) && editingMessageId !== message.id && showActionsForMessage === message.id && (
                  <div className="message-actions">
                    {message.canEdit && onEditMessage && (
                      <button
                        className="message-action-btn"
                        onClick={() => handleStartEdit(message)}
                        title="Редактировать"
                      >
                        <i className="ri-edit-line"></i>
                      </button>
                    )}
                    {message.canDelete && onDeleteMessage && (
                      <button
                        className="message-action-btn message-action-btn--danger"
                        onClick={() => handleDeleteMessage(message.id)}
                        disabled={isDeleting === message.id}
                        title="Удалить"
                      >
                        {isDeleting === message.id ? (
                          <i className="ri-loader-4-line ri-spin"></i>
                        ) : (
                          <i className="ri-delete-bin-line"></i>
                        )}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </ScrollableContainer>

      {/* Поле ввода */}
      <div className="messenger-input">
        {editingMessageId && (
          <div className="edit-indicator">
            <i className="ri-edit-line"></i>
            <span>Редактирование сообщения</span>
            <button
              className="cancel-edit-btn"
              onClick={handleCancelEdit}
              title="Отменить редактирование"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        )}
        <div className="input-container">
          <textarea
            ref={inputRef}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={editingMessageId ? "Редактируйте сообщение..." : placeholder}
            className="message-input"
            rows={1}
            maxLength={maxLength}
            disabled={isSending}
          />
          <div className="input-actions">
            <button
              className="send-button"
              onClick={handleSendMessage}
              disabled={!newMessage.trim() || isSending}
              title={editingMessageId ? "Сохранить изменения" : "Отправить сообщение"}
            >
              {isSending ? (
                <i className="ri-loader-4-line ri-spin"></i>
              ) : (
                <i className="ri-arrow-right-s-line"></i>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Messenger;
